import { Fragment } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { SHORTCUT_GROUPS } from '@/config/shortcuts'

export interface ShortcutsHelpProps {
  open: boolean
  onClose: () => void
}

/**
 * Static cheat-sheet listing every global keyboard shortcut. Opened by `?` or
 * the header Help button. Content comes from {@link SHORTCUT_GROUPS} so it stays
 * in sync with the behaviour in useGlobalShortcuts.
 */
export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg" aria-label="Keyboard shortcuts">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press <Kbd>?</Kbd> any time to reopen this list.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[min(70vh,520px)] space-y-5 overflow-y-auto px-6 py-4">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                {group.title}
              </p>
              <ul className="space-y-1.5">
                {group.shortcuts.map((shortcut, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-4 text-sm text-gray-700 dark:text-gray-200"
                  >
                    <span className="min-w-0 truncate">{shortcut.description}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {shortcut.keys.map((key, k) => (
                        <Fragment key={k}>
                          {k > 0 && (
                            <span className="text-[11px] text-gray-300 dark:text-gray-600">
                              then
                            </span>
                          )}
                          <Kbd>{key}</Kbd>
                        </Fragment>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Kbd({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <kbd className="inline-flex min-w-[22px] items-center justify-center rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-xs font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300">
      {children}
    </kbd>
  )
}

ShortcutsHelp.displayName = 'ShortcutsHelp'
